import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ListSubAdmins, CreateSubAdmin, DeleteSubAdmin, SubAdmin, CreateSubAdminI } from '../../api/api';
import { useIsMainAdmin } from '../../utils/permissions';
import { usePermissionStore } from '../../store/usePermissionStore';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import ComponentCard from '../../components/common/ComponentCard';
import PageMeta from '../../components/common/PageMeta';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '../../components/ui/table/index';
import { Button, Label, Modal, TextInput } from 'flowbite-react';
import { HiOutlineExclamationCircle, HiPlus } from 'react-icons/hi';
import toast from 'react-hot-toast';

const SubAdmins: React.FC = () => {
  const navigate = useNavigate();
  const isMainAdmin = useIsMainAdmin();
  const [subAdmins, setSubAdmins] = useState<SubAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [selectedSubAdmin, setSelectedSubAdmin] = useState<string | null>(null);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [formData, setFormData] = useState<CreateSubAdminI>({
    email: '',
    password: '',
    name: '',
  });

  const isLoadingPermissions = usePermissionStore((state) => state.isLoading);
  const hasFetched = usePermissionStore((state) => state.hasFetched);

  useEffect(() => {
    // Wait for permissions to be fetched before checking
    if (isLoadingPermissions || !hasFetched) {
      return; // Wait for permissions to load
    }

    if (!isMainAdmin) {
      toast.error('Only main admin can access this page');
      navigate('/');
      return;
    }
    fetchSubAdmins();
  }, [isMainAdmin, isLoadingPermissions, hasFetched, navigate]);

  const fetchSubAdmins = async () => {
    setLoading(true);
    const { data, error } = await ListSubAdmins<{ data: { subAdmins: SubAdmin[] } }>();
    if (!error && data?.data) {
      setSubAdmins(data.data.subAdmins || []);
    }
    setLoading(false);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email || !formData.password || !formData.name) {
      toast.error('Please fill in all required fields');
      return;
    }

    const { data, error } = await CreateSubAdmin<{ data: { subAdmin: SubAdmin } }>(formData);
    if (!error && data?.data) {
      toast.success('Sub-admin created successfully');
      setOpenAddModal(false);
      setFormData({ email: '', password: '', name: '' });
      fetchSubAdmins();
    }
  };

  const handleDelete = () => {
    if (selectedSubAdmin !== null) {
      DeleteSubAdmin(selectedSubAdmin).then(({ error }) => {
        if (!error) {
          toast.success('Sub-admin deleted successfully');
          fetchSubAdmins();
        }
        setOpenModal(false);
        setSelectedSubAdmin(null);
      });
    }
  };

  // Show loading while permissions are being fetched
  if (isLoadingPermissions || !hasFetched) {
    return (
      <div className="p-6">
        <div className="text-center py-8">Loading permissions...</div>
      </div>
    );
  }

  if (!isMainAdmin) {
    return null;
  }

  return (
    <>
      <PageMeta title="Sub-Admins" description="Manage sub-admins and their permissions" />
      <div className="p-6">
        <PageBreadcrumb pageTitle="Sub-Admins" />

        <ComponentCard title="Sub-Admin Management" desc="Create and manage sub-admins with specific permissions">
          <div className="flex justify-end mb-4">
            <Button
              onClick={() => setOpenAddModal(true)}
              className="px-4 py-2 text-white rounded-lg hover:bg-[#9A7209] transition-colors font-medium flex items-center justify-center gap-2"
              style={{ fontFamily: 'Lato, sans-serif', backgroundColor: '#B8860B', height: '36px' }}
            >
              <HiPlus className="w-5 h-5" />
              Create Sub-Admin
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03] mt-6">
              <div className="max-w-full overflow-x-auto">
                <Table>
                  <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                    <TableRow>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Name
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Email
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Created By
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-center text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subAdmins.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-gray-500" style={{ fontFamily: 'Lato, sans-serif' }}>
                          No sub-admins found
                        </TableCell>
                      </TableRow>
                    ) : (
                      subAdmins.map((subAdmin) => (
                        <TableRow key={subAdmin._id} className="border-b border-gray-100 dark:border-white/[0.05]">
                          <TableCell className="px-5 py-4 text-sm text-gray-900 dark:text-white/90" style={{ fontFamily: 'Lato, sans-serif' }}>
                            {subAdmin.name || subAdmin.email}
                          </TableCell>
                          <TableCell className="px-5 py-4 text-sm text-gray-900 dark:text-white/90" style={{ fontFamily: 'Lato, sans-serif' }}>
                            {subAdmin.email}
                          </TableCell>
                          <TableCell className="px-5 py-4 text-sm text-gray-900 dark:text-white/90" style={{ fontFamily: 'Lato, sans-serif' }}>
                            {subAdmin.createdBy?.name || subAdmin.createdBy?.email || 'N/A'}
                          </TableCell>
                          <TableCell className="px-5 py-4">
                            <div className="flex gap-2 items-center justify-center">
                              <button
                                onClick={() => navigate(`/sub-admins/${subAdmin._id}`)}
                                className="px-3 py-1.5 text-sm rounded-lg transition-colors font-medium text-white flex items-center justify-center whitespace-nowrap"
                                style={{
                                  fontFamily: 'Lato, sans-serif',
                                  backgroundColor: '#B8860B',
                                  minWidth: '140px',
                                }}
                                onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                                  e.currentTarget.style.backgroundColor = '#9A7209';
                                }}
                                onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                                  e.currentTarget.style.backgroundColor = '#B8860B';
                                }}
                              >
                                Manage Permissions
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedSubAdmin(subAdmin._id);
                                  setOpenModal(true);
                                }}
                                className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center justify-center whitespace-nowrap"
                                style={{ fontFamily: 'Lato, sans-serif', minWidth: '80px' }}
                              >
                                Delete
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </ComponentCard>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        show={openModal}
        size="md"
        position="center"
        onClose={() => {
          setOpenModal(false);
          setSelectedSubAdmin(null);
        }}
        popup
      >
        <Modal.Header />
        <Modal.Body>
          <div className="text-center">
            <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200" />
            <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400" style={{ fontFamily: 'Lato, sans-serif' }}>
              Are you sure you want to delete this sub-admin?
            </h3>
            <div className="flex justify-center gap-4">
              <Button
                onClick={handleDelete}
                className="px-6 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center justify-center"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px' }}
              >
                {"Yes, I'm sure"}
              </Button>
              <Button
                onClick={() => {
                  setOpenModal(false);
                  setSelectedSubAdmin(null);
                }}
                className="px-6 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium flex items-center justify-center"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px' }}
              >
                No, cancel
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Create Sub-Admin Modal */}
      <Modal
        show={openAddModal}
        size="md"
        popup
        onClose={() => {
          setOpenAddModal(false);
          setFormData({ email: '', password: '', name: '' });
        }}
      >
        <Modal.Header />
        <Modal.Body>
          <div className="space-y-6">
            <h3 className="text-xl font-medium text-gray-900 dark:text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
              Create Sub-Admin
            </h3>

            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <div className="mb-2 block">
                  <Label htmlFor="email" value="Email *" style={{ fontFamily: 'Lato, sans-serif' }} />
                </div>
                <TextInput
                  id="email"
                  type="email"
                  placeholder="Enter email address"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>

              <div>
                <div className="mb-2 block">
                  <Label htmlFor="password" value="Password *" style={{ fontFamily: 'Lato, sans-serif' }} />
                </div>
                <TextInput
                  id="password"
                  type="password"
                  placeholder="Enter password (min 6 characters)"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={6}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>

              <div>
                <div className="mb-2 block">
                  <Label htmlFor="name" value="Name *" style={{ fontFamily: 'Lato, sans-serif' }} />
                </div>
                <TextInput
                  id="name"
                  type="text"
                  placeholder="Enter name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setOpenAddModal(false);
                    setFormData({ email: '', password: '', name: '' });
                  }}
                  className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium flex items-center justify-center"
                  style={{ fontFamily: 'Lato, sans-serif', height: '36px', minWidth: '100px' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 text-white rounded-lg hover:bg-[#9A7209] transition-colors font-medium flex items-center justify-center"
                  style={{ fontFamily: 'Lato, sans-serif', backgroundColor: '#B8860B', height: '36px', minWidth: '100px' }}
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default SubAdmins;
